/**
 * **Ganti sandi saya** — tombol di pita atas, untuk SEMUA peran (Imron, 2026-08-07).
 *
 * ### ⛔ Kenapa ia bukan bagian layar "Pengguna konsol"
 * Layar itu hanya untuk `owner`. Kalau ganti-sandi tinggal di sana, `manager` dan `viewer` tak
 * punya cara mengganti sandinya sendiri — dan akuntan yang sandinya bocor harus menunggu
 * pemiliknya, sementara sandinya tetap bocor. ⇒ ia melekat pada ORANGnya, di pita atas, selalu ada.
 *
 * ### ⛔⛔ Sandi lama WAJIB
 * Tanpa itu, satu sesi yang bocor — laptop yang ditinggal terbuka, token yang tersalin — berubah
 * dari akses SEMENTARA jadi pengambilalihan akun PERMANEN: penyerang mengganti sandinya, dan
 * pemilik aslinya terkunci di luar tanpa jalur pemulihan apa pun. Server tetap yang menegakkan;
 * kolomnya di sini hanya membuat penegakan itu terlihat sebelum ditolak.
 */
import { Button, Form, Input, Modal, Typography, message } from 'antd';
import { MIN_PASSWORD_LENGTH } from '@osbpos/contract';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';

export function ChangePassword() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<{ currentPassword: string; newPassword: string; ulang: string }>();

  return (
    <>
      <Button data-testid="ubah-sandi" onClick={() => { setOpen(true); }}>
        {t('password.button')}
      </Button>
      <Modal
        open={open}
        title={t('password.title')}
        okText={t('password.save')}
        data-testid="dialog-sandi"
        onCancel={() => { setOpen(false); form.resetFields(); }}
        onOk={() => {
          void form.validateFields().then(async (v) => {
            try {
              await api.changeOwnPassword(v.currentPassword, v.newPassword);
              void message.success(t('password.ok'));
              setOpen(false);
              form.resetFields();
            } catch (e: unknown) {
              void message.error(e instanceof Error ? e.message : String(e));
            }
          });
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="currentPassword"
            label={t('password.current')}
            rules={[{ required: true }]}
          >
            <Input.Password data-testid="sandi-lama" autoComplete="current-password" />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label={t('password.next')}
            rules={[{ required: true, min: MIN_PASSWORD_LENGTH,
                     message: t('password.weak', { n: MIN_PASSWORD_LENGTH }) }]}
          >
            <Input.Password data-testid="sandi-baru" autoComplete="new-password" />
          </Form.Item>
          {/*
            ⚠️ Ketik ulang: sandi yang salah ketik dua karakter mengunci orangnya keluar dari
            akunnya sendiri, dan yang bisa menolongnya cuma pemilik — yang bisa jadi dirinya.
          */}
          <Form.Item
            name="ulang"
            label={t('password.repeat')}
            dependencies={['newPassword']}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator: (_, v: string) =>
                  v === getFieldValue('newPassword')
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('password.mismatch'))),
              }),
            ]}
          >
            <Input.Password data-testid="sandi-ulang" autoComplete="new-password" />
          </Form.Item>
          <Typography.Text type="secondary">{t('password.hint')}</Typography.Text>
        </Form>
      </Modal>
    </>
  );
}
